import { OpenBetReport } from "app/types/openBetsPanelType";
import { isEmpty } from "lodash";
import type {
  FetchOpenBetsLeagueListPayload,
  FetchOpenBetsMatchListPayload,
  FetchOpenBetsNormalListPayload,
  FetchOpenBetsPanelListPayload,
} from "../../api/openBetsApi";
import * as api from "../../api/openBetsApi";

const DEFAULT_NUMBER_OF_PAGES_WHEN_NO_PAGES = 1;

const OpenBetsListModel = {
  state: {
    openBetsList: [] as Array<any>,
    openBetsTotal: 0,
    openBetsPageTotal: 0,
    currentPage: 1,
    totalStake: 0,
    totalStakeForPage: 0,
    currentView: "NORMAL",
    reportListPerPage: 25,
    isReportListLoading: false,
    page: 0,
    isDetailedView: false,
    openBetsPanelList: [],
    totalPages: 0,
    totalElements: 0,
  },
  reducers: {
    setOpenBetsListRequest(state: Record<string, any>): any {
      return { ...state, isReportListLoading: true };
    },
    setOpenBetsPanelListRequest(state: Record<string, any>): any {
      return {
        ...state,
        isReportListLoading: true,
      };
    },

    resetOpenBetsList(state: Record<string, any>): any {
      return {
        ...state,
        openBetsList: [],
        openBetsTotal: 0,
        openBetsPageTotal: 0,
        totalStake: 0,
        reportListPerPage: 25,
        isReportListLoading: false,
        page: 0,
        isDetailedView: false,
      };
    },

    resetOpenBetsPanelList(state: Record<string, any>): any {
      return {
        ...state,
        openBetsPanelList: [],
        isReportListLoading: false,
        page: 0,
      };
    },

    setOpenBetsListSuccess(
      state: Record<string, any>,
      payload: Record<string, any>
    ): any {
      return {
        ...state,
        isReportListLoading: false,
        openBetsList: payload.openBetsList,
        hasReportListReachedEnd: payload.length === 0,
        totalStake: payload.totalStake,
        totalStakeForPage: payload.totalStakeForPage,
        openBetsTotal: payload.numberOfReports,
        openBetsPageTotal: Math.ceil(payload.numberOfReports / 50),
        currentView: payload.currentView,
        isDetailedView: payload.isDetailedView,
        totalPages: payload.totalPages,
      };
    },
    setOpenBetsPanelListSuccess(
      state: Record<string, any>,
      payload: Record<string, any>
    ): any {
      return {
        ...state,
        isReportListLoading: false,
        openBetsPanelList: payload.openBetsPanelList,
        totalPages: payload.totalPages,
        totalElements: payload.totalElements,
      };
    },
  },
  effects: {
    async loadOpenBetsNormalList(payload: Record<string, any>) {
      this.resetOpenBetsList();
      this.setOpenBetsListRequest();
      const params: FetchOpenBetsNormalListPayload = {
        filter: payload && payload.filter,
        size: payload && payload.size,
        page: payload && payload.page,
        runnerId: payload && payload.runnerId,
        competitionNavNodeId: payload && payload.competitionNavNodeId,
      };
      const isDetailedView =
        (params["runnerId"] ?? 0) > 0 ||
        (params["competitionNavNodeId"] ?? 0) > 0;
      api.fetchOpenBetsNormalList(params).then(resp => {
        const openBetsNormalList = resp.content;
        const totalStake = resp && resp.totalStake;
        const totalStakeForPage = resp && resp.totalStakeForPage;
        const numberOfReports = resp && resp.totalElements;
        // FIXME: totalPages is not returned from the API
        const totalPages =
          resp.totalPages === undefined
            ? DEFAULT_NUMBER_OF_PAGES_WHEN_NO_PAGES
            : resp.totalPages;
        this.setOpenBetsListSuccess({
          openBetsList: openBetsNormalList,
          totalStake: totalStake,
          totalStakeForPage: totalStakeForPage,
          numberOfReports: numberOfReports,
          currentView: "NORMAL",
          isDetailedView: isDetailedView,
          totalPages: totalPages,
        });
      });
    },

    loadAsianOpenBetsPanelList(payload: Record<string, any>, rootState: any) {
      if (!isEmpty(rootState.openBetsList)) {
        this.resetOpenBetsPanelList();
        this.setOpenBetsPanelListRequest();
      }

      const params: FetchOpenBetsPanelListPayload = {
        size: payload && payload.size,
        page: payload && payload.page,
        betType: payload && payload.betType,
      };

      api.fetchAsianOpenBetsPanelList(params).then(resp => {
        const openBetsPanelList = resp.content.map(
          (betReport: OpenBetReport) => {
            return betReport.bet;
          }
        );

        const totalElements = resp && resp.totalElements;
        const totalPages = resp && resp.totalPages;
        this.setOpenBetsPanelListSuccess({
          openBetsPanelList: openBetsPanelList,
          totalPages: totalPages,
          totalElements: totalElements,
        });
      });
    },

    loadOpenBetsPanelList(payload: Record<string, any>, rootState: any) {
      if (!isEmpty(rootState.openBetsList)) {
        this.resetOpenBetsPanelList();
        this.setOpenBetsPanelListRequest();
      }

      const params: FetchOpenBetsPanelListPayload = {
        size: payload && payload.size,
        page: payload && payload.page,
        betType: payload && payload.betType,
      };

      api.fetchOpenBetsPanelList(params).then(resp => {
        const openBetsPanelList = resp.content;
        const totalElements = resp && resp.totalElements;
        const totalPages = resp && resp.totalPages;
        this.setOpenBetsPanelListSuccess({
          openBetsPanelList: openBetsPanelList,
          totalPages: totalPages,
          totalElements: totalElements,
        });
      });
    },

    async loadOpenBetsLeagueList(payload: Record<string, any>) {
      this.resetOpenBetsList();
      this.setOpenBetsListRequest();
      const params: FetchOpenBetsLeagueListPayload = {
        filter: payload && payload.filter,
      };
      api.fetchOpenBetsLeagueList(params).then(resp => {
        const openBetsLeagueList = resp.openBetsPerCompetition;
        const totalStake = resp && resp.totalStake;
        const numberOfReports = resp && resp.totalBetCount;
        // FIXME: totalPages is not returned from the API
        const totalPages =
          resp.totalPages === undefined
            ? DEFAULT_NUMBER_OF_PAGES_WHEN_NO_PAGES
            : resp.totalPages;
        this.setOpenBetsListSuccess({
          openBetsList: openBetsLeagueList,
          totalStake: totalStake,
          totalStakeForPage: 0,
          numberOfReports: numberOfReports,
          currentView: "LEAGUE",
          isDetailedView: false,
          totalPages: totalPages,
        });
      });
    },

    async loadOpenBetsMatchList(payload: Record<string, any>) {
      this.resetOpenBetsList();
      this.setOpenBetsListRequest();
      const params: FetchOpenBetsMatchListPayload = {
        filter: payload && payload.filter,
      };
      params["filter"] = payload && payload.filter;
      api.fetchOpenBetsMatchList(params).then(resp => {
        const openBetsMatchList = resp.events;
        const totalStake = resp && resp.totalStake;
        const numberOfReports = resp && resp.totalBetCount;
        // FIXME: totalPages is not returned from the API
        const totalPages =
          resp.totalPages === undefined
            ? DEFAULT_NUMBER_OF_PAGES_WHEN_NO_PAGES
            : resp.totalPages;
        this.setOpenBetsListSuccess({
          openBetsList: openBetsMatchList,
          totalStake: totalStake,
          totalStakeForPage: 0,
          numberOfReports: numberOfReports,
          currentView: "MATCH",
          isDetailedView: false,
          totalPages,
        });
      });
    },
  },
};
export default OpenBetsListModel;
